import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../context/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { ExternalLink, FileText, Pencil, X } from "lucide-react";

const fmtDatePT = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
};

const Badge = ({ ok, children }) => (
  <span
    className={`inline-flex items-center px-2.5 py-1 text-[10px] tracking-[0.18em] uppercase rounded-sm ${
      ok ? "bg-[#2D6A4F]/15 text-[#2D6A4F]" : "bg-[#9B2226]/15 text-[#9B2226]"
    }`}
  >
    {children}
  </span>
);

const ReportModal = ({ houseId, open, onClose, houseName }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !houseId) return;
    setLoading(true);
    api
      .get(`/admin/house-report/${houseId}`)
      .then((r) => setReport(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [open, houseId]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-3xl bg-[#FCFAF8] border-[#0A1128]/15 rounded-none p-0"
        data-testid="house-report-modal"
      >
        <div className="bg-[#0A1128] text-[#F4F1EB] px-8 py-6">
          <DialogHeader>
            <div className="overline text-[#C5A059] mb-1">Relatório · {houseName}</div>
            <DialogTitle className="font-serif-display text-3xl font-light text-[#F4F1EB]">
              Desempenho mensal
            </DialogTitle>
          </DialogHeader>
        </div>
        <div className="px-8 py-6 max-h-[70vh] overflow-y-auto">
          {loading && <div className="text-sm text-[#3A435E]">A carregar relatório…</div>}
          {!loading && report && (
            <>
              {/* Totals */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                <Stat label="Receita total" value={`€${Math.round(report.totals.earnings).toLocaleString("pt-PT")}`} />
                <Stat label="Reservas" value={report.totals.bookings} />
                <Stat label="Noites" value={report.totals.nights} />
                <Stat label="Hóspedes" value={report.totals.guests} />
                <Stat label="Limpezas" value={report.totals.cleanings} />
                <Stat label="Taxa de ocupação" value={`${report.totals.occupancy_rate}%`} />
              </div>

              {/* Monthly breakdown */}
              <div className="overline mb-3">Por mês</div>
              <div className="border border-[#0A1128]/10">
                <table className="w-full text-sm">
                  <thead className="bg-[#F4F1EB] text-[#3A435E]">
                    <tr>
                      <th className="text-left px-4 py-2 font-normal">Mês</th>
                      <th className="text-right px-4 py-2 font-normal">Receita</th>
                      <th className="text-right px-4 py-2 font-normal">Reservas</th>
                      <th className="text-right px-4 py-2 font-normal">Noites</th>
                      <th className="text-right px-4 py-2 font-normal">Hóspedes</th>
                      <th className="text-right px-4 py-2 font-normal">Limpezas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.monthly.length === 0 && (
                      <tr><td colSpan={6} className="px-4 py-6 text-[#3A435E]">Sem dados.</td></tr>
                    )}
                    {report.monthly.map((m) => (
                      <tr key={m.month} className="border-t border-[#0A1128]/10">
                        <td className="px-4 py-2">{m.month}</td>
                        <td className="px-4 py-2 text-right text-[#C5A059]">€{Math.round(m.earnings).toLocaleString("pt-PT")}</td>
                        <td className="px-4 py-2 text-right">{m.bookings}</td>
                        <td className="px-4 py-2 text-right">{m.nights}</td>
                        <td className="px-4 py-2 text-right">{m.guests}</td>
                        <td className="px-4 py-2 text-right">{m.cleanings}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          <div className="flex justify-end pt-6">
            <button
              onClick={onClose}
              className="border border-[#0A1128]/20 px-5 py-2.5 text-xs tracking-[0.2em] uppercase hover:bg-[#0A1128]/5"
              data-testid="close-report-modal"
            >
              <X size={12} className="inline mr-1" /> Fechar
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Stat = ({ label, value }) => (
  <div className="bg-[#F4F1EB]/60 border border-[#0A1128]/10 p-4">
    <div className="overline text-[10px]">{label}</div>
    <div className="font-serif-display text-2xl text-[#0A1128] mt-1">{value}</div>
  </div>
);

const ClientesTable = ({ houses = [], onEditHouse }) => {
  const [reportFor, setReportFor] = useState(null);

  // Count houses per owner identity (email > tax_number > name)
  const housesByOwner = useMemo(() => {
    const map = new Map();
    houses.forEach((h) => {
      const key = (h.owner_email || h.tax_number || h.owner_name || "—").toLowerCase().trim();
      map.set(key, (map.get(key) || 0) + 1);
    });
    return map;
  }, [houses]);

  if (houses.length === 0) {
    return (
      <div className="bg-[#FCFAF8] border border-[#0A1128]/10 px-6 py-12 text-center text-[#3A435E]">
        Ainda não existem casas/clientes. Adicione uma casa na tab &ldquo;Casas&rdquo;.
      </div>
    );
  }

  return (
    <>
      {/* Title block — matching screenshot */}
      <div className="bg-[#0A1128] text-[#F4F1EB] px-8 py-8 mb-6 flex items-center gap-5">
        <div className="w-14 h-14 border border-[#C5A059]/60 grid place-items-center">
          <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="#C5A059" strokeWidth="1.5">
            <path d="M3 21V8l5-4 5 4v13M14 21v-9h6v9M3 21h18" />
          </svg>
        </div>
        <div>
          <div className="overline text-[#C5A059]">Admin</div>
          <h2 className="font-serif-display text-3xl md:text-4xl font-light text-[#F4F1EB]">Lista de clientes</h2>
        </div>
      </div>

      <div className="bg-[#FCFAF8] border border-[#0A1128]/10 overflow-x-auto" data-testid="clientes-table">
        <table className="w-full text-sm min-w-[1800px]">
          <thead className="bg-[#0A1128]/95 text-[#F4F1EB]">
            <tr>
              <th className="text-left px-4 py-3 font-normal text-xs tracking-[0.15em] uppercase">Nome</th>
              <th className="text-left px-4 py-3 font-normal text-xs tracking-[0.15em] uppercase">Cód. chave</th>
              <th className="text-center px-4 py-3 font-normal text-xs tracking-[0.15em] uppercase">Nº casas</th>
              <th className="text-center px-4 py-3 font-normal text-xs tracking-[0.15em] uppercase">Manutenção</th>
              <th className="text-left px-4 py-3 font-normal text-xs tracking-[0.15em] uppercase">Inscrição</th>
              <th className="text-left px-4 py-3 font-normal text-xs tracking-[0.15em] uppercase">Morada da casa</th>
              <th className="text-left px-4 py-3 font-normal text-xs tracking-[0.15em] uppercase">C. Postal</th>
              <th className="text-left px-4 py-3 font-normal text-xs tracking-[0.15em] uppercase">Link</th>
              <th className="text-center px-4 py-3 font-normal text-xs tracking-[0.15em] uppercase">Relatório</th>
              <th className="text-left px-4 py-3 font-normal text-xs tracking-[0.15em] uppercase">BI / CC</th>
              <th className="text-left px-4 py-3 font-normal text-xs tracking-[0.15em] uppercase">NIF</th>
              <th className="text-left px-4 py-3 font-normal text-xs tracking-[0.15em] uppercase">Nascimento</th>
              <th className="text-left px-4 py-3 font-normal text-xs tracking-[0.15em] uppercase">Morada pessoal</th>
              <th className="text-left px-4 py-3 font-normal text-xs tracking-[0.15em] uppercase">C. Postal</th>
              <th className="text-left px-4 py-3 font-normal text-xs tracking-[0.15em] uppercase">Email</th>
              <th className="text-left px-4 py-3 font-normal text-xs tracking-[0.15em] uppercase">Contacto</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {houses.map((h, idx) => {
              const ownerKey = (h.owner_email || h.tax_number || h.owner_name || "—").toLowerCase().trim();
              const numHouses = housesByOwner.get(ownerKey) || 1;
              return (
                <tr
                  key={h.id}
                  className={`border-t border-[#0A1128]/10 hover:bg-[#F4F1EB]/40 ${idx % 2 === 0 ? "bg-[#FCFAF8]" : "bg-[#F4F1EB]/20"}`}
                  data-testid={`cliente-row-${h.id}`}
                >
                  <td className="px-4 py-3 text-[#0A1128] font-medium">{h.owner_name || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs text-[#3A435E]">{h.key_code || "—"}</td>
                  <td className="px-4 py-3 text-center">
                    <span className="inline-flex w-7 h-7 items-center justify-center bg-[#0A1128] text-[#F4F1EB] text-xs">
                      {numHouses}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Badge ok={h.has_maintenance}>{h.has_maintenance ? "Sim" : "Não"}</Badge>
                  </td>
                  <td className="px-4 py-3 text-xs text-[#3A435E]">{fmtDatePT(h.signup_date)}</td>
                  <td className="px-4 py-3 text-xs max-w-[200px] truncate" title={h.address || ""}>{h.address || "—"}</td>
                  <td className="px-4 py-3 text-xs">{h.postal_code || "—"}</td>
                  <td className="px-4 py-3 text-xs">
                    {h.external_link ? (
                      <a
                        href={h.external_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[#C5A059] hover:underline"
                        data-testid={`external-link-${h.id}`}
                      >
                        <ExternalLink size={12} /> {h.name || "Abrir"}
                      </a>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => setReportFor(h)}
                      className="inline-flex items-center gap-1.5 border border-[#0A1128]/20 px-3 py-1.5 text-xs hover:bg-[#0A1128] hover:text-white transition-colors"
                      data-testid={`open-report-${h.id}`}
                    >
                      <FileText size={12} /> Relatório
                    </button>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{h.citizen_card_number || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{h.tax_number || "—"}</td>
                  <td className="px-4 py-3 text-xs text-[#3A435E]">{fmtDatePT(h.owner_birth_date)}</td>
                  <td className="px-4 py-3 text-xs max-w-[200px] truncate" title={h.owner_personal_address || ""}>
                    {h.owner_personal_address || "—"}
                  </td>
                  <td className="px-4 py-3 text-xs">{h.owner_personal_postal_code || "—"}</td>
                  <td className="px-4 py-3 text-xs">
                    {h.owner_email ? (
                      <a href={`mailto:${h.owner_email}`} className="text-[#0A1128] hover:text-[#C5A059]">{h.owner_email}</a>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {h.owner_phone ? (
                      <a href={`tel:${h.owner_phone}`} className="text-[#0A1128] hover:text-[#C5A059]">{h.owner_phone}</a>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => onEditHouse?.(h)}
                      className="text-[#3A435E] hover:text-[#C5A059]"
                      title="Editar dados"
                      data-testid={`edit-cliente-${h.id}`}
                    >
                      <Pencil size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ReportModal
        houseId={reportFor?.id}
        houseName={reportFor?.name || ""}
        open={!!reportFor}
        onClose={() => setReportFor(null)}
      />
    </>
  );
};

export default ClientesTable;
